import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowRight, MapPin, Phone, MessageCircle, Loader2, User } from "lucide-react";
import { waLink } from "@/config/siteConfig";
import { useSiteSettings, getPhones, getWaNumber, telHref } from "@/lib/siteSettings";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { MobileBar } from "@/components/MobileBar";
import { Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fromDb = (p) => ({
  id: p.id, title: p.title, location: p.location, price: p.price,
  status: p.featured ? "FEATURED" : p.listingType === "Rent" ? "FOR RENT" : "NEW LISTING",
  category: p.propertyType, listingType: p.listingType,
  gallery: (p.images || []).map((path) => `${API}/uploads/${path}`),
  chips: [
    p.bhk && `${p.bhk} BHK`,
    p.areaSqft && `${p.areaSqft} sq.ft.`,
    p.floor && `Floor: ${p.floor}`,
    p.facing && `${p.facing} facing`,
    p.furnishing,
  ].filter(Boolean),
  society: p.society, contactPerson: p.contactPerson, contactNumber: p.contactNumber,
  description: p.description,
});

export default function PropertyPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { site } = useSiteSettings();
  const [dbProp, setDbProp] = useState(null);
  const [failed, setFailed] = useState(false);
  const [active, setActive] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
    setActive(0);
    setDbProp(null);
    setFailed(false);
    axios
      .get(`${API}/properties/${id}`)
      .then(({ data }) => setDbProp(fromDb(data)))
      .catch(() => setFailed(true));
  }, [id]);

  const property = dbProp;

  if (!property) {
    return (
      <div className="min-h-screen bg-warm">
        <Header />
        <div className="max-w-[1280px] mx-auto px-4 py-24 text-center" data-testid="property-not-found">
          {failed ? (
            <>
              <h1 className="font-display text-3xl font-semibold text-navy">Property not found</h1>
              <p className="mt-3 text-ink-soft">This listing may have been removed or unpublished.</p>
              <button
                onClick={() => navigate("/")}
                className="mt-8 bg-gold text-navy hover:bg-gold-dark transition-colors rounded-lg px-7 py-3.5 text-[15px] font-semibold"
                data-testid="property-not-found-back"
              >
                Back to Properties
              </button>
            </>
          ) : (
            <Loader2 className="animate-spin mx-auto text-gold" size={30} />
          )}
        </div>
        <Footer />
        <MobileBar />
      </div>
    );
  }

  const message = `I'm interested in "${property.title}" (${property.location}) — ${property.price}. Please share more details.`;
  const callHref = property.contactNumber
    ? `tel:+91${property.contactNumber.replace(/\D/g, "").slice(-10)}`
    : telHref(getPhones(site)[0].number);
  const callLabel = property.contactNumber || getPhones(site)[0].number;

  const enquire = () => {
    sessionStorage.setItem(
      "rk_prefill",
      JSON.stringify({ interest: property.listingType === "Rent" ? "Rent" : "Buy", message })
    );
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-warm pb-[64px] md:pb-0" data-testid="property-page">
      <Header />
      <main className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <button
          onClick={() => navigate("/")}
          data-testid="property-back-button"
          className="inline-flex items-center gap-2 text-[13px] font-semibold text-ink-soft hover:text-navy transition-colors"
        >
          <ArrowLeft size={15} /> All Properties
        </button>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-[1.55fr_1fr] gap-8 lg:gap-12 items-start">
          <Reveal>
            <div>
              <div className="relative rounded-xl overflow-hidden aspect-[16/10] bg-navy/5" data-testid="property-gallery-main">
                {property.gallery.length > 0 && (
                  <img
                    key={property.gallery[active]}
                    src={property.gallery[active]}
                    alt={`${property.title} — photo ${active + 1}`}
                    className="w-full h-full object-cover"
                  />
                )}
                <span className="absolute top-4 left-4 bg-navy/90 backdrop-blur-sm text-gold text-[10px] font-bold tracking-[0.18em] px-3 py-1.5 rounded-md">
                  {property.status}
                </span>
              </div>
              {property.gallery.length > 1 && (
                <div className="mt-3 grid grid-cols-4 sm:grid-cols-5 gap-3" data-testid="property-gallery-thumbs">
                  {property.gallery.map((src, i) => (
                    <button
                      key={src + i}
                      onClick={() => setActive(i)}
                      data-testid={`property-gallery-thumb-${i}`}
                      className={`relative aspect-[4/3] rounded-lg overflow-hidden transition-all duration-300 ${
                        i === active ? "ring-2 ring-gold ring-offset-2 ring-offset-warm" : "opacity-70 hover:opacity-100"
                      }`}
                      aria-label={`Photo ${i + 1}`}
                    >
                      <img src={src} alt="" loading="lazy" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <aside className="bg-white rounded-xl border border-line p-7 sm:p-8 lg:sticky lg:top-24" data-testid="property-summary-card">
              <span className="text-[10px] font-bold tracking-[0.22em] uppercase text-gold">
                {property.category} · {property.listingType === "Rent" ? "For Rent" : "For Sale"}
              </span>
              <h1 className="mt-3 font-display text-2xl sm:text-3xl font-semibold text-navy leading-snug">{property.title}</h1>
              {property.society && <p className="mt-1.5 text-[13px] font-medium text-ink/70">{property.society}</p>}
              <p className="mt-2 flex items-center gap-1.5 text-sm text-ink-soft">
                <MapPin size={14} className="text-gold" /> {property.location}
              </p>
              <p className="mt-5 font-display text-3xl font-semibold text-gold" data-testid="property-page-price">{property.price}</p>
              {property.chips.length > 0 && (
                <div className="mt-5 flex flex-wrap gap-2">
                  {property.chips.map((c) => (
                    <span key={c} className="text-[12px] font-semibold bg-warm border border-line rounded-md px-3 py-1.5">{c}</span>
                  ))}
                </div>
              )}
              {property.contactPerson && (
                <p className="mt-5 flex items-center gap-2 text-[13px] text-ink-soft">
                  <User size={14} className="text-gold" /> Contact: <span className="font-semibold text-ink">{property.contactPerson}</span>
                </p>
              )}
              <button
                onClick={enquire}
                data-testid="property-page-enquire-button"
                className="mt-6 w-full bg-gold text-navy hover:bg-gold-dark hover:-translate-y-0.5 transition-all duration-300 rounded-lg px-6 py-4 text-[15px] font-semibold flex items-center justify-center gap-2 shadow-md shadow-gold/25"
              >
                Enquire About This Property <ArrowRight size={16} />
              </button>
              <a
                href={waLink(`Hi RK Real Estate, ${message}`, getWaNumber(site))}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="property-page-whatsapp-button"
                className="mt-3 w-full border border-[#25D366]/60 text-[#128C4A] hover:bg-[#25D366]/10 transition-colors rounded-lg px-6 py-3.5 text-[14px] font-semibold flex items-center justify-center gap-2"
              >
                <MessageCircle size={16} /> Chat on WhatsApp
              </a>
              <a
                href={callHref}
                data-testid="property-page-call-button"
                className="mt-3 w-full border border-line text-ink hover:border-gold hover:text-navy transition-colors rounded-lg px-6 py-3.5 text-[14px] font-semibold flex items-center justify-center gap-2"
              >
                <Phone size={16} className="text-gold" /> {callLabel}
              </a>
            </aside>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <section className="mt-12 max-w-3xl" data-testid="property-description">
            <h2 className="font-display text-xl sm:text-2xl font-semibold text-navy">About This Property</h2>
            <p className="mt-4 text-sm sm:text-base text-ink-soft leading-relaxed whitespace-pre-line">
              {property.description ||
                `${property.title} is located in ${property.location}. Connect with RK Real Estate for complete details, documentation and site visit coordination for this property.`}
            </p>
            <p className="mt-3 text-sm sm:text-base text-ink-soft leading-relaxed">
              Our team can arrange a guided site visit and share transparent information so you can decide with confidence.
            </p>
          </section>
        </Reveal>
      </main>
      <Footer />
      <MobileBar />
    </div>
  );
}
